import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { meetingRoomsApi, type MeetingEvent, type MeetingRoom } from '../services/api';
import logger from '../utils/logger';

type RoomSchedule = {
  room: MeetingRoom;
  events: MeetingEvent[];
};

type RoomFilter = 'all' | 'available' | 'busy';

function dateKey(date: Date) {
  return date.toISOString().slice(0, 10);
}

function startOfDay(offset = 0) {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offset);
  return date;
}

function formatTime(value: string, locale: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--:--';
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatDuration(event: MeetingEvent, isArabic: boolean) {
  const start = new Date(event.startTime).getTime();
  const end = new Date(event.endTime).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return '';
  const minutes = Math.round((end - start) / 60000);
  if (minutes < 60) return isArabic ? `${minutes} د` : `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest
    ? isArabic
      ? `${hours} س ${rest} د`
      : `${hours}h ${rest}m`
    : isArabic
    ? `${hours} س`
    : `${hours}h`;
}

function currentEvent(events: MeetingEvent[]) {
  const now = Date.now();
  return events.find((event) => {
    const start = new Date(event.startTime).getTime();
    const end = new Date(event.endTime).getTime();
    return start <= now && now < end && event.status !== 'free';
  });
}

function nextEvent(events: MeetingEvent[]) {
  const now = Date.now();
  return events
    .filter((event) => new Date(event.startTime).getTime() > now && event.status !== 'free')
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())[0];
}

export default function MeetingRoomScheduleScreen({ navigation }: any) {
  const { i18n } = useTranslation();
  const { theme } = useTheme();
  const isArabic = i18n.language === 'ar';
  const locale = isArabic ? 'ar-SA' : 'en-US';

  const [selectedDate, setSelectedDate] = useState(dateKey(startOfDay()));
  const [filter, setFilter] = useState<RoomFilter>('all');
  const [schedule, setSchedule] = useState<RoomSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const days = useMemo(
    () =>
      Array.from({ length: 7 }).map((_, index) => {
        const date = startOfDay(index);
        return {
          key: dateKey(date),
          label:
            index === 0
              ? isArabic
                ? 'اليوم'
                : 'Today'
              : index === 1
              ? isArabic
                ? 'غدًا'
                : 'Tomorrow'
              : new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date),
          day: new Intl.DateTimeFormat(locale, { day: '2-digit' }).format(date),
        };
      }),
    [isArabic, locale],
  );

  const loadSchedule = useCallback(async () => {
    try {
      setLoading(true);
      const rooms = await meetingRoomsApi.list();
      const activeRooms = rooms.filter((room) => room.isActive !== false);
      const results = await Promise.all(
        activeRooms.map(async (room) => ({
          room,
          events: await meetingRoomsApi.getEvents(room.id, selectedDate).catch(() => []),
        })),
      );
      setSchedule(results);
    } catch (error: any) {
      logger.error('Failed to load meeting room schedule:', error);
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        error?.response?.data?.message ||
          (isArabic ? 'تعذر تحميل جدول غرف الاجتماعات' : 'Unable to load meeting room schedule'),
      );
    } finally {
      setLoading(false);
    }
  }, [isArabic, selectedDate]);

  useEffect(() => {
    navigation.setOptions({
      title: isArabic ? 'جدول غرف الاجتماعات' : 'Meeting Room Schedule',
      headerTintColor: '#ffffff',
      headerTitleStyle: { color: '#ffffff', fontWeight: '700' },
    });
  }, [navigation, isArabic]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const refresh = async () => {
    setRefreshing(true);
    await loadSchedule();
    setRefreshing(false);
  };

  const summary = useMemo(() => {
    const busy = schedule.filter((item) => currentEvent(item.events)).length;
    const available = schedule.length - busy;
    const meetings = schedule.reduce((total, item) => total + item.events.length, 0);
    return { busy, available, meetings };
  }, [schedule]);

  const filteredSchedule = useMemo(() => {
    return schedule.filter((item) => {
      const busy = Boolean(currentEvent(item.events));
      if (filter === 'available') return !busy;
      if (filter === 'busy') return busy;
      return true;
    });
  }, [filter, schedule]);

  const renderRoom = ({ item }: { item: RoomSchedule }) => {
    const active = currentEvent(item.events);
    const upcoming = nextEvent(item.events);
    const sortedEvents = [...item.events].sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    );

    return (
      <View
        style={[
          styles.roomCard,
          { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
        ]}
      >
        <View style={styles.roomHeader}>
          <View style={styles.roomTitleGroup}>
            <View
              style={[
                styles.roomStatusDot,
                { backgroundColor: active ? theme.colors.error : theme.colors.success },
              ]}
            />
            <Text style={[styles.roomName, { color: theme.colors.text }]} numberOfLines={1}>
              {item.room.name}
            </Text>
          </View>
          <View
            style={[
              styles.roomStatusPill,
              { backgroundColor: (active ? theme.colors.error : theme.colors.success) + '18' },
            ]}
          >
            <Text
              style={[
                styles.roomStatusText,
                { color: active ? theme.colors.error : theme.colors.success },
              ]}
            >
              {active ? (isArabic ? 'مشغولة' : 'Busy') : isArabic ? 'متاحة' : 'Available'}
            </Text>
          </View>
        </View>

        <Text style={[styles.roomEmail, { color: theme.colors.textSecondary }]} numberOfLines={1}>
          {item.room.roomEmail}
        </Text>

        <View style={[styles.nowPanel, { backgroundColor: theme.colors.background }]}>
          <Ionicons
            name={active ? 'radio-button-on' : 'checkmark-circle-outline'}
            size={20}
            color={active ? theme.colors.error : theme.colors.success}
          />
          <View style={{ flex: 1 }}>
            <Text style={[styles.nowTitle, { color: theme.colors.text }]}>
              {active
                ? active.subject
                : upcoming
                ? isArabic
                  ? 'لا يوجد اجتماع الآن'
                  : 'No meeting right now'
                : isArabic
                ? 'اليوم متاح'
                : 'Open for the day'}
            </Text>
            <Text style={[styles.nowMeta, { color: theme.colors.textSecondary }]}>
              {active
                ? `${formatTime(active.startTime, locale)} - ${formatTime(active.endTime, locale)}`
                : upcoming
                ? `${isArabic ? 'القادم' : 'Next'} ${formatTime(upcoming.startTime, locale)}`
                : isArabic
                ? 'لا توجد حجوزات قادمة'
                : 'No upcoming bookings'}
            </Text>
          </View>
        </View>

        {sortedEvents.length > 0 ? (
          <View style={styles.timeline}>
            {sortedEvents.map((event) => (
              <View key={event.id} style={styles.eventRow}>
                <View style={styles.timeCol}>
                  <Text style={[styles.eventTime, { color: theme.colors.text }]}>
                    {formatTime(event.startTime, locale)}
                  </Text>
                  <Text style={[styles.eventDuration, { color: theme.colors.textSecondary }]}>
                    {formatDuration(event, isArabic)}
                  </Text>
                </View>
                <View style={[styles.eventMarker, { backgroundColor: theme.colors.primary }]} />
                <View style={styles.eventBody}>
                  <Text
                    style={[styles.eventSubject, { color: theme.colors.text }]}
                    numberOfLines={2}
                  >
                    {event.subject || (isArabic ? 'اجتماع' : 'Meeting')}
                  </Text>
                  <Text
                    style={[styles.eventMeta, { color: theme.colors.textSecondary }]}
                    numberOfLines={1}
                  >
                    {event.organizer || item.room.name}
                    {typeof event.attendeeCount === 'number' ? ` • ${event.attendeeCount}` : ''}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.emptyEvents}>
            <Ionicons name="calendar-clear-outline" size={24} color={theme.colors.textSecondary} />
            <Text style={[styles.emptyEventsText, { color: theme.colors.textSecondary }]}>
              {isArabic ? 'لا توجد حجوزات لهذا اليوم' : 'No bookings for this day'}
            </Text>
          </View>
        )}
      </View>
    );
  };

  if (loading) {
    return (
      <View style={[styles.center, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={[styles.hero, { backgroundColor: theme.colors.primary }]}>
        <View>
          <Text style={styles.heroLabel}>{isArabic ? 'غرف الاجتماعات' : 'Meeting rooms'}</Text>
          <Text style={styles.heroTitle}>{isArabic ? 'جدول اليوم' : "Today's schedule"}</Text>
        </View>
        <View style={styles.heroStats}>
          <Text style={styles.heroStatValue}>{summary.available}</Text>
          <Text style={styles.heroStatLabel}>{isArabic ? 'متاحة' : 'available'}</Text>
        </View>
      </View>

      <View style={styles.summaryRow}>
        <MiniStat
          label={isArabic ? 'مشغولة' : 'Busy'}
          value={summary.busy}
          color={theme.colors.error}
          theme={theme}
        />
        <MiniStat
          label={isArabic ? 'حجوزات' : 'Bookings'}
          value={summary.meetings}
          color={theme.colors.info}
          theme={theme}
        />
        <MiniStat
          label={isArabic ? 'غرف' : 'Rooms'}
          value={schedule.length}
          color={theme.colors.primary}
          theme={theme}
        />
      </View>

      <View style={styles.scheduleControls}>
        <ScrollView
          horizontal
          style={styles.daysScroller}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.daysRow}
        >
          {days.map((day) => {
            const active = day.key === selectedDate;
            return (
              <TouchableOpacity
                key={day.key}
                style={[
                  styles.dayChip,
                  {
                    backgroundColor: active ? theme.colors.primary : theme.colors.card,
                    borderColor: active ? theme.colors.primary : theme.colors.border,
                  },
                ]}
                onPress={() => setSelectedDate(day.key)}
              >
                <Text
                  style={[
                    styles.dayLabel,
                    { color: active ? '#f8fafc' : theme.colors.textSecondary },
                  ]}
                  numberOfLines={1}
                  adjustsFontSizeToFit
                  minimumFontScale={0.78}
                >
                  {day.label}
                </Text>
                <Text style={[styles.dayNumber, { color: active ? '#f8fafc' : theme.colors.text }]}>
                  {day.day}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        <View style={styles.filterRow}>
          {(['all', 'available', 'busy'] as RoomFilter[]).map((item) => {
            const active = filter === item;
            const label =
              item === 'all'
                ? isArabic
                  ? 'الكل'
                  : 'All'
                : item === 'available'
                ? isArabic
                  ? 'متاحة'
                  : 'Available'
                : isArabic
                ? 'مشغولة'
                : 'Busy';
            return (
              <TouchableOpacity
                key={item}
                style={[
                  styles.filterChip,
                  {
                    backgroundColor: active ? theme.colors.primary : theme.colors.card,
                    borderColor: theme.colors.border,
                  },
                ]}
                onPress={() => setFilter(item)}
              >
                <Text
                  style={[styles.filterText, { color: active ? '#f8fafc' : theme.colors.text }]}
                >
                  {label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      <FlatList
        data={filteredSchedule}
        keyExtractor={(item) => item.room.id}
        renderItem={renderRoom}
        contentContainerStyle={styles.list}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} />}
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <Ionicons name="business-outline" size={54} color={theme.colors.textSecondary} />
            <Text style={[styles.emptyStateText, { color: theme.colors.textSecondary }]}>
              {isArabic ? 'لا توجد غرف مطابقة' : 'No matching rooms'}
            </Text>
          </View>
        }
      />
    </View>
  );
}

function MiniStat({
  label,
  value,
  color,
  theme,
}: {
  label: string;
  value: number;
  color: string;
  theme: ReturnType<typeof useTheme>['theme'];
}) {
  return (
    <View
      style={[
        styles.miniStat,
        { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
      ]}
    >
      <Text style={[styles.miniValue, { color }]}>{value}</Text>
      <Text style={[styles.miniLabel, { color: theme.colors.textSecondary }]}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  hero: {
    margin: 16,
    marginBottom: 10,
    borderRadius: 16,
    padding: 18,
    minHeight: 112,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  heroLabel: { color: '#dbeafe', fontSize: 13, fontWeight: '700' },
  heroTitle: { color: '#f8fafc', fontSize: 25, fontWeight: '900', marginTop: 6 },
  heroStats: { alignItems: 'center', justifyContent: 'center' },
  heroStatValue: { color: '#f8fafc', fontSize: 34, fontWeight: '900' },
  heroStatLabel: { color: '#dbeafe', fontSize: 12, fontWeight: '700' },
  summaryRow: { flexDirection: 'row', gap: 10, paddingHorizontal: 16, marginBottom: 10 },
  miniStat: {
    flex: 1,
    minHeight: 62,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  miniValue: { fontSize: 20, fontWeight: '900' },
  miniLabel: { fontSize: 12, fontWeight: '700', marginTop: 2 },
  scheduleControls: { width: '100%', overflow: 'hidden' },
  daysScroller: { width: '100%', flexGrow: 0 },
  daysRow: { paddingHorizontal: 16, gap: 8, paddingBottom: 10 },
  dayChip: {
    width: 72,
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: 10,
    alignItems: 'center',
  },
  dayLabel: { fontSize: 11, fontWeight: '800' },
  dayNumber: { fontSize: 18, fontWeight: '900', marginTop: 3 },
  filterRow: { flexDirection: 'row', gap: 8, paddingHorizontal: 16, paddingBottom: 10 },
  filterChip: { borderWidth: 1, borderRadius: 999, paddingHorizontal: 14, paddingVertical: 8 },
  filterText: { fontSize: 12, fontWeight: '800' },
  list: { padding: 16, paddingTop: 4, gap: 14 },
  roomCard: { borderWidth: 1, borderRadius: 16, padding: 15 },
  roomHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  roomTitleGroup: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 },
  roomStatusDot: { width: 10, height: 10, borderRadius: 5 },
  roomName: { flex: 1, fontSize: 17, fontWeight: '900' },
  roomStatusPill: { borderRadius: 999, paddingHorizontal: 10, paddingVertical: 5 },
  roomStatusText: { fontSize: 11, fontWeight: '900' },
  roomEmail: { marginTop: 5, fontSize: 12, fontWeight: '600' },
  nowPanel: {
    marginTop: 13,
    borderRadius: 12,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  nowTitle: { fontSize: 14, fontWeight: '900' },
  nowMeta: { fontSize: 12, fontWeight: '700', marginTop: 3 },
  timeline: { marginTop: 12, gap: 12 },
  eventRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  timeCol: { width: 58 },
  eventTime: { fontSize: 12, fontWeight: '900' },
  eventDuration: { fontSize: 10, fontWeight: '700', marginTop: 2 },
  eventMarker: { width: 8, height: 8, borderRadius: 4, marginTop: 5 },
  eventBody: { flex: 1 },
  eventSubject: { fontSize: 13, fontWeight: '800' },
  eventMeta: { fontSize: 11, fontWeight: '600', marginTop: 3 },
  emptyEvents: {
    marginTop: 12,
    minHeight: 70,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  emptyEventsText: { fontSize: 12, fontWeight: '700' },
  emptyState: { alignItems: 'center', paddingTop: 80 },
  emptyStateText: { marginTop: 12, fontSize: 15, fontWeight: '800' },
});
